import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideRouter, withRouterConfig } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak
} from 'keycloak-angular';

import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { PartsEffects } from './modules/parts/store/parts.effects';
import { partsReducer } from './modules/parts/store/parts.reducer';
import { ApiModule, Configuration } from './shared/generated';
import { LoaderInterceptor } from './shared/interceptors/loader.interceptor';
import { SharedEffects } from './shared/store/shared.effects';
import { sharedReducer } from './shared/store/shared.reducer';

// Create interceptor condition for API calls
const escapedApiUrl = environment.api_url.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
const apiCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: new RegExp('^' + escapedApiUrl + '.*', 'i')
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideKeycloak({
      config: {
        url: environment.identity_url,
        realm: environment.realm,
        clientId: 'angular-app'
      },
      initOptions: {
        onLoad: 'login-required',
        checkLoginIframe: false,
        useNonce: globalThis.location.hostname.includes('localhost')
      },
      providers: [
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [apiCondition]
        }
      ]
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Angular 22 changed the default to 'always'; keep the pre-v22 behaviour.
    provideRouter(appRoutes, withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' })),
    provideHttpClient(
      withXhr(),
      withInterceptors([includeBearerTokenInterceptor]),
      withInterceptorsFromDi()
    ),
    importProvidersFrom(
      StoreModule.forRoot(
        {},
        {
          runtimeChecks: {
            strictActionImmutability: true,
            strictStateImmutability: true
          }
        }
      ),
      StoreModule.forFeature('PartsState', partsReducer),
      StoreModule.forFeature('SharedState', sharedReducer),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production
      }),
      EffectsModule.forRoot(),
      EffectsModule.forFeature([PartsEffects, SharedEffects]),
      ApiModule.forRoot(() => {
        return new Configuration({
          basePath: environment.api_url
        });
      })
    ),
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 2500,
        verticalPosition: 'top',
        horizontalPosition: 'right'
      }
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic', appearance: 'outline' }
    }
  ]
};
